import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Filter, FileText, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteQueue = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  useEffect(() => {
    const fetchWaybills = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/waybills?page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        
        // Map filter to actual statuses
        if (statusFilter === 'pending') {
          url += `&status=quote_requested`;
        } else if (statusFilter === 'in_costing') {
          url += `&status=in_costing`;
        } else if (statusFilter === 'quote_ready') {
          url += `&status=quote_ready`;
        } else if (statusFilter !== 'all') {
          url += `&status=${statusFilter}`;
        }

        const response = await axios.get(url);
        setWaybills(response.data.waybills);
        setTotal(response.data.total);
      } catch (err) {
        console.error('Error fetching waybills:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaybills();
  }, [page, search, statusFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  const statuses = [
    { value: 'pending', label: 'Pending Quote' },
    { value: 'in_costing', label: 'In Costing' },
    { value: 'quote_ready', label: 'Quote Ready' },
    { value: 'all', label: 'All' },
  ];

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="quote-queue-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Quote Queue</h1>
            <p className="text-slate-600 mt-1">Review and price customer waybill requests</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="card-elevated mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by waybill number, company..."
                  className="pl-10"
                  data-testid="quote-queue-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="quote-queue-filter">
                  <Filter className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="card-elevated">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="card-title">Submitted Waybills</CardTitle>
                <p className="text-sm text-slate-500">{total} records</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : waybills.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Waybill ID</th>
                        <th>Customer</th>
                        <th>Sender</th>
                        <th>Route</th>
                        <th>Submitted</th>
                        <th>Items</th>
                        <th>Weight</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waybills.map((wb) => {
                        const detailLink = `/admin/shipment/${wb.waybill_id}`;
                        return (
                        <tr 
                          key={wb.waybill_id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => navigate(detailLink, { state: { from: '/admin/quote-queue' } })}
                        >
                          <td>
                            <span className="text-teal-700 font-medium">{wb.reference_number}</span>
                          </td>
                          <td>
                            <div>
                              <p className="font-medium text-slate-900">{wb.customer?.company_name || '-'}</p>
                            </div>
                          </td>
                          <td>{wb.sender_company}</td>
                          <td>
                            <span>{wb.origin_city}</span>
                            <span className="text-slate-400 mx-1">→</span>
                            <span>{wb.destination_city}</span>
                          </td>
                          <td>{formatDate(wb.submission_date)}</td>
                          <td className="text-center">{wb.total_items || wb.items?.length || 0}</td>
                          <td>{(wb.total_weight || 0).toFixed(1)} kg</td>
                          <td><StatusBadge status={wb.status} /></td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                        const pageNum = page <= 2 ? i + 1 : page - 1 + i;
                        if (pageNum > totalPages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            className={page === pageNum ? 'bg-teal-700' : ''}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No waybills found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default QuoteQueue;
